import React, {useEffect, useState} from 'react';
import {Input, Button, Select, InputNumber, DatePicker, Radio} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const {Search} = Input;

export const DateRangeFilter = ({
                                    setSelectedKeys,
                                    selectedKeys,
                                    confirm,
                                    clearFilters,
                                }) => {
    const [rangeValue, setRangeValue] = useState([null, null]);

    useEffect(() => {
        if (selectedKeys[0]) {
            try {
                const { start, end } = JSON.parse(selectedKeys[0]);
                const startDayjs = start ? dayjs(start) : null;
                const endDayjs = end ? dayjs(end) : null;
                setRangeValue([startDayjs, endDayjs]);
            } catch (err) {
            }
        }
    }, [selectedKeys]);

    const handleChange = (values) => {
        // RangePicker returns dayjs objects
        setRangeValue(values);
    };

    const handleSearch = () => {
        const [startDayjs, endDayjs] = rangeValue;
        if (startDayjs && endDayjs) {
            const startFullDay = startDayjs.startOf('day');
            const endFullDay = endDayjs.endOf('day');

            const startISO = startFullDay.toDate().toISOString();
            const endISO   = endFullDay.toDate().toISOString();

            setSelectedKeys([
                JSON.stringify({ start: startISO, end: endISO }),
            ]);
        } else {
            setSelectedKeys([]);
        }
        confirm({ closeDropdown: false });
    };

    const handleReset = () => {
        setRangeValue([null, null]);
        clearFilters();
        confirm({ closeDropdown: false });
    };

    return (
        <div style={{ padding: 8 }}>
            <RangePicker
                value={rangeValue}
                onChange={handleChange}
                style={{ display: 'block', marginBottom: 8 }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
                <Button type="primary" onClick={handleSearch} size="small">
                    Search
                </Button>
                <Button onClick={handleReset} size="small">
                    Reset
                </Button>
            </div>
        </div>
    );
};

export const PaymentMethodFilter = ({
                                        setSelectedKeys,
                                        selectedKeys = [],
                                        confirm,
                                        clearFilters
                                    }) => {
    const [selectedValue, setSelectedValue] = useState('');

    useEffect(() => {
        if (selectedKeys[0]) {
            setSelectedValue(selectedKeys[0]);
        }
    }, [selectedKeys]);

    const handleRadioChange = (e) => {
        const value = e.target.value;
        setSelectedValue(value);

        if (value === '') {
            clearFilters?.();
            confirm({ closeDropdown: false });
        } else {
            setSelectedKeys([value]);
            confirm({ closeDropdown: false });
        }
    };

    return (
        <div style={{ padding: 8 }}>
            <Radio.Group onChange={handleRadioChange} value={selectedValue}>
                <Radio value="">ყველა</Radio>
                <Radio value="true">TBC</Radio>
                <Radio value="false">ანგარიშით</Radio>
            </Radio.Group>
        </div>
    );
};

export const FilterSearch = ({selectedKeys, setSelectedKeys, confirm, clearFilters, placeholder = "Search"}) => {
    return (
        <div style={{padding: 8, display: 'flex', alignItems: 'center', gap: '4px'}}>
            <Search
                placeholder={placeholder}
                value={selectedKeys[0] || ''}
                onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                onSearch={() => confirm({closeDropdown: false})}
                style={{display: 'block'}}
            />
            <Button
                onClick={() => {
                    clearFilters && clearFilters();
                    confirm({closeDropdown: false});
                }}
                size="small"
                style={{width: 32, height: 32, borderRadius: '6px'}}
            >
                X
            </Button>
        </div>
    );
};

export const FilterDropdown = ({
                                   setSelectedKeys,
                                   selectedKeys,
                                   confirm,
                                   clearFilters,
                                   placeholder = "Search",
                                   data
                               }) => {
    return (
        <div style={{
            padding: 8,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            minWidth: '266px',
            maxWidth: '400px'
        }}>
            <Select
                mode="multiple"
                style={{width: '100%'}}
                placeholder="Select Status"
                value={selectedKeys}
                onChange={newSelectedKeys => {
                    setSelectedKeys(newSelectedKeys)
                    confirm({closeDropdown: false})
                }}
                onBlur={() => confirm({closeDropdown: false})}
                onSelect={() => confirm({closeDropdown: false})}
                filterOption={(input, option) =>
                    // Case-insensitive matching of search input with status name
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
            >
                {data?.map(item => (
                    <Select.Option key={item.id} value={item.id}>
                        {item.name ? item.name : `${item.firstName} ${item.lastName}`}
                    </Select.Option>
                ))}
            </Select>
            <Button
                onClick={() => {
                    clearFilters && clearFilters(); // Clears the filter
                    confirm({closeDropdown: false});
                }}
                size="small"
                style={{width: 32, height: 32, borderRadius: '6px'}}
            >
                X
            </Button>
        </div>
    );
}


export const NumberRangeFilter = ({setSelectedKeys, selectedKeys, confirm, clearFilters}) => {
    const [min, setMin] = useState();
    const [max, setMax] = useState();

    const handleSearch = () => {
        let minValue = min;
        let maxValue = max;

        // Adjust min and max if only one is provided
        if (min !== undefined && max === undefined) {
            maxValue = minValue;
        } else if (max !== undefined && min === undefined) {
            minValue = maxValue;
        }

        // Swap values if minValue > maxValue
        if (minValue !== undefined && maxValue !== undefined && minValue > maxValue) {
            [minValue, maxValue] = [maxValue, minValue];
        }

        setSelectedKeys([JSON.stringify({min: minValue, max: maxValue})]);
        confirm({closeDropdown: false});
    };

    const handleReset = () => {
        setMin();
        setMax();
        clearFilters();
        confirm({closeDropdown: false});
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div style={{padding: 8}}>
            <div style={{display: 'flex', height: '32px'}}>
                <InputNumber
                    placeholder="Min"
                    value={min}
                    onChange={value => setMin(value)}
                    style={{width: '100px'}}
                    onKeyDown={handleKeyDown}
                />
                <InputNumber
                    placeholder="Max"
                    value={max}
                    onChange={value => setMax(value)}
                    style={{width: '100px'}}
                    onKeyDown={handleKeyDown}
                />
                <div style={{display: 'flex'}}>
                    <Button onClick={() => {
                        handleSearch();
                    }} icon={<SearchOutlined/>}/>
                    <Button
                        onClick={handleReset}
                        size="small"
                        style={{width: 32, height: 32, borderRadius: '6px'}}
                    >
                        X
                    </Button>
                </div>
            </div>
        </div>
    );
};


export const exportToExcel = ({
                                  data,
                                  columns,
                                  fileName,
                                  coaches = [],
                                  teams = [],
                                  actionBasedStatuses = [],
                                  nonActionBasedStatuses = [],
                                  shouldAddTotal = false,
                              }) => {
    // Helper function to map ID fields to their corresponding names
    const mapIdToName = (id, dataList, fields = []) => {
        if (!id || !dataList) return 'Unknown';  // Return 'Unknown' if no ID or dataList is provided
        const item = dataList.find(d => d.id === id); // Find item by ID
        if (!item) return 'Unknown'; // If no item is found, return 'Unknown'

        let name = '';
        fields.forEach((field, index) => {
            if (item[field]) {
                name += item[field]; // Concatenate field values
            } else {
                name += 'Unknown'; // If field is missing, append 'Unknown'
            }
            // Add space between firstName and lastName (or between any other fields)
            if (index < fields.length - 1) name += ' ';
        });

        return name;
    };

    // Map data to match columns
    const exportData = data.map(item => {
        const row = {};
        columns.forEach(col => {
            // Handle coachId to full name mapping
            if (col.dataIndex === 'coachId') {
                row[col.title] = mapIdToName(item.coachId, coaches, ['firstName', 'lastName']) || 'No Coach';
            }
            // Handle teamId to team name mapping
            else if (col.dataIndex === 'teamId') {
                row[col.title] = mapIdToName(item.teamId, teams, ['name']) || 'No Team';
            }
            // Handle statusId to status name mapping
            else if (col.dataIndex === 'statusId') {
                row[col.title] = mapIdToName(item.statusId, nonActionBasedStatuses, ['name']) || 'No Status';
            } else if (col.dataIndex === 'actionBasedStatusId') {
                row[col.title] = mapIdToName(item.actionBasedStatusId, actionBasedStatuses, ['name']) || 'No Status';
            } else {
                // For all other columns, simply use the data as is
                row[col.title] = item[col.dataIndex];
            }
        });
        return row;
    });
    if (shouldAddTotal) {
        const totalAmount = data.reduce((acc, item) => acc + (Number(item.amount) || 0), 0);
        const totalRow = {};
        columns.forEach((col) => {
            totalRow[col.title] = '';
        });
        totalRow['თანხა'] = totalAmount;
        totalRow[columns[0].title] = 'Total';
        exportData.push(totalRow);
    }

    // Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Players');

    // Generate buffer
    const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});

    // Create a Blob from the buffer
    const dataBlob = new Blob([excelBuffer], {type: 'application/octet-stream'});

    // Save the file using file-saver
    saveAs(dataBlob, `${fileName}.xlsx`);
};